
import React from "react";
import { Conversation } from "../types";
import { ConversationCard } from "./ConversationCard";
import { ConversationFilters } from "./ConversationFilters";
import { useConversationFilters } from "../hooks/useConversationFilters";
import { Search } from "lucide-react";

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
}

export const ConversationList = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
}: ConversationListProps) => {
  const {
    filters,
    filteredConversations,
    activeFiltersCount,
    setSearchQuery,
    setStatusFilter,
    setPriorityFilter,
    setParticipantFilter,
    setDateRange,
    clearAllFilters,
  } = useConversationFilters(conversations);

  // Calculate stats
  const unreadCount = filteredConversations.filter(c => c.unread).length;

  return (
    <div className="flex flex-col h-full bg-white">
      <ConversationFilters
        searchQuery={filters.searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={filters.statusFilter}
        onStatusFilterChange={setStatusFilter}
        priorityFilter={filters.priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        participantFilter={filters.participantFilter}
        onParticipantFilterChange={setParticipantFilter}
        dateRange={filters.dateRange}
        onDateRangeChange={setDateRange}
        onClearFilters={clearAllFilters}
        activeFiltersCount={activeFiltersCount}
      />
      
      {/* Conversation stats */}
      <div className="px-4 py-2 bg-gray-50 border-b">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {filteredConversations.length} of {conversations.length} conversations
          </span>
          {unreadCount > 0 && (
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
              {unreadCount} unread
            </span>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.map((conversation) => (
          <ConversationCard
            key={conversation.id}
            conversation={conversation}
            isSelected={selectedConversationId === conversation.id}
            onClick={() => onSelectConversation(conversation.id)}
          />
        ))}
        
        {filteredConversations.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No conversations found</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {activeFiltersCount > 0 
                ? "No conversations match your current filters. Try adjusting your search criteria."
                : "No conversations found. Check back later or adjust your filters."
              }
            </p>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
